import mixpanel from 'mixpanel-browser';

import { detectAccessMode, detectBrowser, detectPlatform } from '../device';
import type { AnalyticsUser, EventProps, SuperProperties } from '../types';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
const CACHE_RECOVERY_EVENTS_KEY = 'kuden_cache_recovery_events';

let sdkInitialized = false;
let pwaLifecycleListenerAttached = false;

/**
 * Mixpanel SDK を匿名で初期化する（ユーザー識別前でも安全に track できる状態にする）。
 *
 * mixpanel.track() は init 前に呼ぶと config 未設定で例外になるため、
 * ログイン前に発火する認証イベント等に備えて、SDK の初期化だけを先行して行う。
 * 二重初期化はモジュールレベルのフラグで防ぐ。
 *
 * @returns 検知したスーパープロパティ。トークン未設定時は null。
 */
export function ensureMixpanelInitialized(): SuperProperties | null {
  if (!MIXPANEL_TOKEN) return null;

  const accessMode = detectAccessMode();
  const devicePlatform = detectPlatform();
  const deviceBrowser = detectBrowser();

  if (sdkInitialized) {
    return { accessMode, devicePlatform, deviceBrowser };
  }
  sdkInitialized = true;

  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] device info:', { accessMode, devicePlatform, deviceBrowser });
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: false,
    track_pageview: true,
    ignore_dnt: true,
    persistence: 'localStorage',
  });

  // デバイス情報をスーパープロパティとして登録（全イベントに自動付与）
  mixpanel.register({ accessMode, devicePlatform, deviceBrowser });

  // PWA ライフサイクル検知（appinstalled + display-mode 変化）
  setupPwaLifecycleTracking();

  // CacheRecoveryからキューイングされたイベントを送信
  flushQueuedCacheRecoveryEvents();

  return { accessMode, devicePlatform, deviceBrowser };
}

/**
 * サインイン済みユーザーで Mixpanel のユーザー識別・属性設定を行う。
 * SDK が未初期化なら先に初期化する。
 *
 * @returns 検知したスーパープロパティ。トークン未設定・ユーザー未確定時は null。
 */
export function identifyMixpanel(user: AnalyticsUser): SuperProperties | null {
  if (!MIXPANEL_TOKEN || !user.id) return null;

  const superProps = ensureMixpanelInitialized();
  if (!superProps) return null;

  const { accessMode, devicePlatform, deviceBrowser } = superProps;

  // ユーザー識別
  mixpanel.identify(user.id);

  const now = new Date().toISOString();

  const organizationName = user.organization?.name;
  const departmentName = user.department?.departmentName;
  const userRole = user.role;

  // 履歴を配列に追加（重複自動排除）
  // 空値・undefined は配列に含めない（[] を渡すと union は何も追加しない）
  mixpanel.people.union({
    accessModesUsed: [accessMode],
    devicePlatformsUsed: [devicePlatform],
    deviceBrowsersUsed: [deviceBrowser],
    organizationsUsed: organizationName ? [organizationName] : [],
    departmentsUsed: departmentName ? [departmentName] : [],
    rolesUsed: userRole ? [userRole] : [],
  });

  // 初回値のみ記録（既存値があれば無視される）
  mixpanel.people.set_once({
    firstAccessMode: accessMode,
    firstDevicePlatform: devicePlatform,
    firstDeviceBrowser: deviceBrowser,
    firstOrganization: organizationName,
    firstDepartment: departmentName,
    firstRole: userRole,
    firstSeenAt: now,
  });

  // 固定属性 + 既存プロパティ温存 + 最新値
  mixpanel.people.set({
    $name: `${user.familyName?.value} ${user.givenName?.value}`,
    $email: user.email,
    $avatar: user.thumbnail,
    userId: user.id,
    organization: organizationName,
    department: departmentName,
    role: userRole,
    accessMode,
    devicePlatform,
    deviceBrowser,
    lastAccessMode: accessMode,
    lastDevicePlatform: devicePlatform,
    lastDeviceBrowser: deviceBrowser,
    lastOrganization: organizationName,
    lastDepartment: departmentName,
    lastRole: userRole,
    lastSeenAt: now,
  });

  return superProps;
}

/**
 * Mixpanel にイベントを送信する。
 * SDK 未初期化なら何もしない（init 前 track による例外を防ぐ）。
 * スーパープロパティは init 時の register により自動付与されるため props はそのまま渡す。
 */
export function trackMixpanel(eventName: string, properties?: EventProps): void {
  if (!MIXPANEL_TOKEN || !sdkInitialized) return;
  mixpanel.track(eventName, properties);
}

/**
 * PWA インストールおよび display-mode 変化を検知し、Mixpanel にトラッキングする。
 * モジュールレベルフラグで二重登録を防ぐ。
 */
function setupPwaLifecycleTracking(): void {
  if (pwaLifecycleListenerAttached) return;
  pwaLifecycleListenerAttached = true;

  window.addEventListener('appinstalled', () => {
    mixpanel.track('PWA Installed');
    mixpanel.people.set({ pwaInstalledAt: new Date().toISOString() });
  });

  // ブラウザ → standalone への切替を検知
  const standaloneMedia = window.matchMedia('(display-mode: standalone)');
  standaloneMedia.addEventListener('change', e => {
    if (!e.matches) return;
    mixpanel.track('PWA Launched', { from: 'mode_change' });
    mixpanel.register({ accessMode: 'pwa' });
    mixpanel.people.union({ accessModesUsed: ['pwa'] });
    mixpanel.people.set({ lastAccessMode: 'pwa', lastSeenAt: new Date().toISOString() });
  });
}

/**
 * CacheRecovery によりキューイングされたイベントを送信し、キューをクリアする。
 */
function flushQueuedCacheRecoveryEvents(): void {
  try {
    const queuedEventsJson = localStorage.getItem(CACHE_RECOVERY_EVENTS_KEY);
    if (!queuedEventsJson) return;

    const queuedEvents = JSON.parse(queuedEventsJson) as Array<{
      event: string;
      properties: Record<string, unknown>;
      queuedAt: number;
    }>;

    if (queuedEvents.length > 0) {
      queuedEvents.forEach(item => {
        mixpanel.track(item.event, {
          ...item.properties,
          queuedAt: new Date(item.queuedAt).toISOString(),
          sentAt: new Date().toISOString(),
          delayMs: Date.now() - item.queuedAt,
        });
      });
      localStorage.removeItem(CACHE_RECOVERY_EVENTS_KEY);
    }
  } catch (e) {
    console.warn('[Analytics] CacheRecoveryイベント送信失敗:', e);
  }
}
